import React, { useRef, useState, useEffect } from 'react';
import { FlightTable } from '../components/masterSchedule/FlightTable';
import { RecordFormModal, DeleteConfirmModal } from '../components/masterSchedule/Modals';
import { RecordFormModalRef } from '../components/masterSchedule/Modals/RecordFormModal/RecordFormModal';
import { validateRecord } from '../utils/types';
import { customToast } from '../utils/toast';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useAppDispatch, useAppSelector } from '../services/redux/hooks';
import {
  useGetMasterFlightsQuery,
  useCreateMasterFlightMutation,
  useUpdateMasterFlightMutation,
  useDeleteMasterFlightMutation,
  useLazyCheckDuplicateFlightQuery,
} from '../services/redux/api';
import {
  openCreateForm,
  openVariantForm,
  openEditForm,
  openAdhocForm,
  closeForm,
  setActiveTabAction,
  setDraftAction,
} from '../services/redux/slices/formSlice';
import {
  openDeleteConfirm,
  closeDeleteConfirm,
} from '../services/redux/slices/uiSlice';
import type { MasterFlightRecord } from '../utils/types';
import { cloneRecordForEdit } from '../utils/types';
import { stripCodesharesFromStations } from '../utils/codeshareHelpers';
import { showValidationToasts } from '../utils/validationMessages';
import { useCodeshareSyncManager } from '../hooks/useCodeshareSyncManager';

export default function MasterSchedule() {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1); // Reset to page 1 on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = useGetMasterFlightsQuery({
    page,
    limit,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  });
  const records = data?.data || [];
  const totalRecords = data?.meta.total || 0;
  const totalPages = data?.meta.totalPages || 0;

  const [createFlight] = useCreateMasterFlightMutation();
  const [updateFlight] = useUpdateMasterFlightMutation();
  const [deleteFlight] = useDeleteMasterFlightMutation();
  const [checkDuplicate] = useLazyCheckDuplicateFlightQuery();
  const { syncCodeshares } = useCodeshareSyncManager();

  const { isOpen, editMode, isVariantMode, isAdhocMode, activeTab, draft } = useAppSelector(state => state.form);
  const { showDeleteConfirm, pendingDeleteId, pendingDeleteFlightNo } = useAppSelector(state => state.ui);
  const formModalRef = useRef<RecordFormModalRef>(null);

  const handleSetActiveTab = (tab: React.SetStateAction<'basic' | 'stations' | 'preview'>) => {
    dispatch(setActiveTabAction(tab));
  };

  const handleSetDraft = (newDraft: React.SetStateAction<MasterFlightRecord>) => {
    dispatch(setDraftAction(newDraft));
  };

  const handleOpenAdd = () => {
    dispatch(openCreateForm());
    setTimeout(() => formModalRef.current?.resetValidation(), 0);
  };

  const handleOpenAdhoc = () => {
    dispatch(openAdhocForm());
    setTimeout(() => formModalRef.current?.resetValidation(), 0);
  };

  const handleOpenEdit = (record: MasterFlightRecord) => {
    dispatch(openEditForm(cloneRecordForEdit(record)));
    setTimeout(() => formModalRef.current?.resetValidation(), 0);
  };

  const handleCreateVariant = (record: MasterFlightRecord) => {
    const variantDraft = {
      ...cloneRecordForEdit(record),
      _id: undefined,
      csfsKey: '',
      itineraryVarId: 1, // Auto-assigned on save via duplicate check
      stations: record.stations || [],
      scheduleStatus: record.scheduleStatus || 'ACTIVE',
      freqRate: '',
      createdAt: undefined,
      updatedAt: undefined,
      deletedAt: undefined,
    };

    dispatch(openVariantForm(variantDraft));
    setTimeout(() => formModalRef.current?.resetValidation(), 0);
  };

  const handleCloseForm = () => {
    dispatch(closeForm());
    formModalRef.current?.resetValidation();
  };

  const handleSave = async () => {
    const validationError = validateRecord(draft);
    if (validationError) {
      console.warn('Validation failed:', validationError);
      formModalRef.current?.triggerValidation();
      showValidationToasts(draft);
      return;
    }

    try {
      let recordToSave = { ...draft };
      recordToSave.freqRate = '';

      // Auto-assign variant ID for new flights (per-season or global pattern)
      if (!editMode) {
        try {
          const flightNoDigits = draft.flightNo.replace(/[^0-9]/g, '');
          const duplicateResult = await checkDuplicate({
            flightOwner: draft.flightOwner,
            flightNo: flightNoDigits,
            seasonId: draft.seasonId,
          }).unwrap();
          recordToSave.itineraryVarId = duplicateResult.nextAvailableVariant;
        } catch (error) {
          console.warn('Duplicate check failed, defaulting to variant 1:', error);
          recordToSave.itineraryVarId = 1;
        }
      }

      // Strip codeshares before save — they're persisted separately via codeshare API
      const flightWithoutCodeshares = stripCodesharesFromStations(recordToSave);

      let savedFlightId: string;
      if (editMode) {
        await updateFlight({ id: draft._id!, record: flightWithoutCodeshares }).unwrap();
        savedFlightId = draft._id!;
      } else {
        const result = await createFlight(flightWithoutCodeshares).unwrap();
        savedFlightId = result._id || '';
      }

      if (!savedFlightId) {
        console.error('Failed to get flight ID after save');
        return;
      }

      await syncCodeshares(recordToSave, savedFlightId);
      if (!editMode) setPage(1);
      dispatch(closeForm());
    } catch (err: any) {
      const msg = err?.data?.message || err?.message || '';
      if (/zero operating dates/i.test(msg) || /no operating dates/i.test(msg)) {
        customToast.error('This frequency produces no operating dates in the selected date range. Please adjust the frequency or dates.');
      } else if (/overlap/i.test(msg) || /conflicting variant/i.test(msg)) {
        customToast.error(`Variant date overlap: ${msg}`);
      } else {
        customToast.error(msg || 'Failed to save flight record.');
      }
      console.error('Save error:', err);
    }
  };

  const handleDelete = (id: string, flightNo: string) => {
    dispatch(openDeleteConfirm({ id, flightNo }));
  };

  const handleCancelDelete = () => {
    dispatch(closeDeleteConfirm());
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId || !pendingDeleteFlightNo) return;

    try {
      await deleteFlight({ id: pendingDeleteId, flightNo: pendingDeleteFlightNo }).unwrap();
      dispatch(closeDeleteConfirm());
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden text-gray-900 font-sans">
      {/* Main */}
      <main className="flex-1 p-4 w-full min-h-0 overflow-auto">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center min-h-[400px] text-gray-500">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-500 rounded-full mx-auto mb-4 animate-spin" />
              <p className="text-lg font-medium">Loading flight schedules...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-danger-50 border border-danger-100 rounded-lg px-5 py-4 mb-6 text-danger-600">
            <div className="flex items-start gap-3">
              <WarningAmberIcon sx={{ fontSize: 28 }} />
              <div className="flex-1">
                <p className="m-0 font-semibold mb-1">Error loading data</p>
                <p className="m-0 text-lg">
                  {(error as any)?.data?.message || (error as any)?.error || 'Failed to load data'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Flight Table */}
        {!isLoading && !error && (
          <FlightTable
            records={records}
            onEdit={handleOpenEdit}
            onDelete={(id) => {
              const record = records.find(r => r._id === id);
              if (record) handleDelete(id, record.flightNo);
            }}
            onCreateVariant={handleCreateVariant}
            onAdd={handleOpenAdd}
            onAddAdhoc={handleOpenAdhoc}
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            currentPage={page}
            totalPages={totalPages}
            totalRecords={totalRecords}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
          />
        )}
      </main>

      {/* Modals */}
      <RecordFormModal
        ref={formModalRef}
        open={isOpen}
        editMode={editMode}
        isVariantMode={isVariantMode}
        isAdhocMode={isAdhocMode}
        activeTab={activeTab}
        setActiveTab={handleSetActiveTab}
        draft={draft}
        setDraft={handleSetDraft}
        error={null}
        onClose={handleCloseForm}
        onSave={handleSave}
      />

      <DeleteConfirmModal
        open={showDeleteConfirm}
        pendingKey={pendingDeleteId}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
